import React, { useState, useRef, useEffect } from 'react';
import { X, Play, Pause, Volume2, Brain, Moon, Activity } from 'lucide-react';

interface SamplePlayerModalProps {
  onClose: () => void;
}

const SAMPLES = [
  {
    id: 'sleep',
    title: 'Deep Sleep (Delta Waves)',
    description: 'Binaural beats at 2Hz combined with deep brown noise for restorative sleep.',
    icon: Moon,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10',
    baseFreq: 100,
    beatFreq: 2,
    noiseType: 'brown'
  },
  {
    id: 'focus',
    title: 'Intense Focus (Gamma Waves)',
    description: '40Hz binaural tones with light white noise for deep work.',
    icon: Brain,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    baseFreq: 200,
    beatFreq: 40,
    noiseType: 'white'
  },
  {
    id: 'relax',
    title: 'Anxiety Relief (Alpha Waves)',
    description: '10Hz binaural beats with gentle pink noise to lower cortisol.',
    icon: Activity,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    baseFreq: 150,
    beatFreq: 10,
    noiseType: 'pink'
  }
];

export default function SamplePlayerModal({ onClose }: SamplePlayerModalProps) {
  const [activeTrack, setActiveTrack] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const audioCtxRef = useRef<AudioContext | null>(null);
  const activeNodesRef = useRef<any[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      stopAudio();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(console.error);
      }
    };
  }, [onClose]);

  const stopAudio = () => {
    activeNodesRef.current.forEach(node => {
      try {
        if (node.stop) node.stop();
        if (node.disconnect) node.disconnect();
      } catch (e) {}
    });
    activeNodesRef.current = [];
  };

  const playAudio = (sampleId: string) => {
    stopAudio();
    
    // Initialize AudioContext on first user interaction
    if (!audioCtxRef.current) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.error("Web Audio API is not supported in this browser");
        return;
      }
      audioCtxRef.current = new AudioContextClass();
    }
    
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const sample = SAMPLES.find(s => s.id === sampleId);
    if (!sample) return;

    // Master Gain (Volume Control)
    const masterGain = ctx.createGain();
    masterGain.gain.value = 0; // Start at 0 for fade in
    
    // Smooth fade in to avoid clicking
    masterGain.gain.setValueAtTime(0, ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 1);
    
    masterGain.connect(ctx.destination);
    activeNodesRef.current.push(masterGain);

    // Left Ear Oscillator
    const oscL = ctx.createOscillator();
    oscL.type = 'sine';
    oscL.frequency.value = sample.baseFreq - (sample.beatFreq / 2);
    
    const panL = ctx.createStereoPanner ? ctx.createStereoPanner() : ctx.createPanner();
    if (panL instanceof StereoPannerNode) {
      panL.pan.value = -1;
    } else {
      (panL as PannerNode).setPosition(-1, 0, 0);
    }
    
    oscL.connect(panL);
    panL.connect(masterGain);
    oscL.start();
    activeNodesRef.current.push(oscL, panL);

    // Right Ear Oscillator
    const oscR = ctx.createOscillator();
    oscR.type = 'sine';
    oscR.frequency.value = sample.baseFreq + (sample.beatFreq / 2);
    
    const panR = ctx.createStereoPanner ? ctx.createStereoPanner() : ctx.createPanner();
    if (panR instanceof StereoPannerNode) {
      panR.pan.value = 1;
    } else {
      (panR as PannerNode).setPosition(1, 0, 0);
    }
    
    oscR.connect(panR);
    panR.connect(masterGain);
    oscR.start();
    activeNodesRef.current.push(oscR, panR);

    // Noise Generator (for texture)
    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = buffer;
    noiseSource.loop = true;
    
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'lowpass';
    
    // Adjust filter based on noise type
    if (sample.noiseType === 'brown') {
      noiseFilter.frequency.value = 400;
    } else if (sample.noiseType === 'pink') {
      noiseFilter.frequency.value = 1000;
    } else {
      noiseFilter.frequency.value = 8000;
    }
    
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.05; // Keep noise subtle
    
    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(masterGain);
    noiseSource.start();
    
    activeNodesRef.current.push(noiseSource, noiseFilter, noiseGain);
  };

  const togglePlay = (id: string) => {
    if (activeTrack === id) {
      if (isPlaying) {
        stopAudio();
        setIsPlaying(false);
      } else {
        playAudio(id);
        setIsPlaying(true);
      }
    } else {
      setActiveTrack(id);
      setIsPlaying(true);
      playAudio(id);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="relative w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div>
            <h2 id="modal-title" className="text-xl font-medium text-white">
              Sample Audio Library
            </h2>
            <p className="text-sm text-zinc-400 mt-1">Experience the power of real-time brainwave entrainment.</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-zinc-400" aria-hidden="true" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {SAMPLES.map((sample) => {
            const Icon = sample.icon;
            const isActive = activeTrack === sample.id;
            
            return (
              <div 
                key={sample.id}
                className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${isActive ? 'bg-zinc-900/80 border-emerald-500/30' : 'bg-zinc-900/30 border-white/5 hover:border-white/10'}`}
              >
                <div className={`w-12 h-12 rounded-xl ${sample.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-6 h-6 ${sample.color}`} aria-hidden="true" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-white truncate">{sample.title}</h3>
                  <p className="text-sm text-zinc-400 truncate">{sample.description}</p>
                </div>
                
                <button
                  onClick={() => togglePlay(sample.id)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${isActive && isPlaying ? 'bg-emerald-500 text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  aria-label={isActive && isPlaying ? "Pause sample" : "Play sample"}
                >
                  {isActive && isPlaying ? (
                    <Pause className="w-5 h-5" aria-hidden="true" />
                  ) : (
                    <Play className="w-5 h-5 ml-1" aria-hidden="true" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
        
        {activeTrack && (
          <div className="bg-emerald-500/10 border-t border-emerald-500/20 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Volume2 className="w-5 h-5 text-emerald-400 animate-pulse" />
              <span className="text-sm font-medium text-emerald-400">
                {isPlaying ? 'Synthesizing frequencies...' : 'Paused'}
              </span>
            </div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div 
                  key={i} 
                  className={`w-1 bg-emerald-400 rounded-full ${isPlaying ? 'animate-pulse' : 'opacity-30'}`}
                  style={{ 
                    height: `${Math.max(8, Math.random() * 24)}px`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: '0.8s'
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
